import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import NewClientModal from "../clients/NewClientModal";
import { listClients, createClient, getClientById } from "../../api/clients";
import { createTicket } from "../../api/tickets";
import { APPOINTMENT_TYPE_OPTIONS } from "../../constants/appointmentTypes";
import {
  modalBase,
  modalTitle,
  labelCls,
  fieldCls,
  btnCls,
} from "../../ui/rescheduleModalStyles";
import ModalCloseX from "../common/ModalCloseX";
import { digitsOnly, formatNANP, toNANPDigits10 } from "../../utils/phone";
import { countSearchLetters } from "../../utils/normalizeSearch";
import { buildNameMatcher } from "../../utils/nameSearchMatcher";
import { useAuth } from "../../context/AuthContext";

const HOUSE_ACCOUNT_NAME = "house account";

const isHouseAccountClient = (client) => {
  const fullName =
    client?.full_name ||
    `${client?.first_name || ""} ${client?.last_name || ""}`.trim();
  return (fullName || "").trim().toLowerCase() === HOUSE_ACCOUNT_NAME;
};

function formatDisplayDate(date) {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatDateValue(date, timeZone) {
  if (!date) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function NewTicketModal({ isOpen, onClose, defaultDate }) {
  const navigate = useNavigate();
  const { salonTimezone } = useAuth();
  const [step, setStep] = useState("search");
  const [ticketDate, setTicketDate] = useState(defaultDate || new Date());
  const [calendarViewDate, setCalendarViewDate] = useState(
    defaultDate || new Date()
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [appointmentType, setAppointmentType] = useState("walk_in");
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientPrefill, setNewClientPrefill] = useState(null);
  const [serviceLogOpen, setServiceLogOpen] = useState(false);
  const [serviceLogClient, setServiceLogClient] = useState(null);
  const [serviceLogLoading, setServiceLogLoading] = useState(false);
  const [serviceLogError, setServiceLogError] = useState("");
  const [createError, setCreateError] = useState("");
  const [dropdownMaxHeightPx, setDropdownMaxHeightPx] = useState(240);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const searchTokenRef = useRef(0);
  const clientSearchWrapRef = useRef(null);
  const modalPanelRef = useRef(null);
  const modalBodyRef = useRef(null);
  const dropdownRef = useRef(null);
  const calendarAnchorRef = useRef(null);
  const calendarPopoverRef = useRef(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!isOpen) return;
    setStep("search");
    setTicketDate(defaultDate || new Date());
    setCalendarViewDate(defaultDate || new Date());
    setShowCalendar(false);
    setQuery("");
    setResults([]);
    setSearching(false);
    setSearchError("");
    setIsSearchDropdownOpen(false);
    setSelectedClient(null);
    setAppointmentType("walk_in");
    setServiceLogOpen(false);
    setServiceLogClient(null);
    setServiceLogLoading(false);
    setServiceLogError("");
    setCreateError("");
    setNewClientPrefill(null);
  }, [defaultDate, isOpen]);

  const trimmedSearch = query.trim();
  const termDigits = digitsOnly(trimmedSearch);
  const termDigits10 = toNANPDigits10(trimmedSearch);
  const letterCount = countSearchLetters(trimmedSearch);
  const hasLetters = letterCount > 0;
  const isNameSearch = hasLetters && letterCount >= 3;
  const isPhoneSearch = !hasLetters && termDigits.length >= 4;
  const hasTerm = trimmedSearch.length > 0;
  const shouldShowDropdown =
    isOpen && step === "search" && isSearchDropdownOpen && hasTerm;

  useEffect(() => {
    if (!isOpen || step !== "search") return;
    if (!(isNameSearch || isPhoneSearch)) {
      searchTokenRef.current += 1;
      setResults([]);
      setSearchError("");
      setSearching(false);
      return;
    }

    const searchId = ++searchTokenRef.current;
    setSearching(true);
    setSearchError("");
    const timer = setTimeout(async () => {
      try {
        const searchValue = isPhoneSearch ? termDigits : trimmedSearch;
        const data = await listClients({
          search: searchValue,
          limit: 50,
          exclude_house: true,
        });
        if (searchTokenRef.current !== searchId) return;
        const list = Array.isArray(data) ? data : [];
        const nameMatches = isNameSearch ? buildNameMatcher(trimmedSearch) : null;
        const filtered = list.filter((client) => {
          if (isHouseAccountClient(client)) return false;
          if (nameMatches) {
            const fullName =
              client?.full_name ||
              `${client?.first_name || ""} ${client?.last_name || ""}`.trim();
            return nameMatches(fullName);
          }
          return true;
        });
        setResults(filtered);
      } catch (err) {
        if (searchTokenRef.current !== searchId) return;
        console.error(err);
        setSearchError("Unable to search clients.");
        setResults([]);
      } finally {
        if (searchTokenRef.current === searchId) {
          setSearching(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [isOpen, isNameSearch, isPhoneSearch, step, termDigits, trimmedSearch]);

  useEffect(() => {
    if (!showCalendar) return;

    const updatePosition = () => {
      const anchor = calendarAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };

    const handlePointerDown = (event) => {
      const anchor = calendarAnchorRef.current;
      const popover = calendarPopoverRef.current;
      if (!anchor || !popover) return;
      if (anchor.contains(event.target) || popover.contains(event.target)) return;
      setShowCalendar(false);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [showCalendar]);

  useEffect(() => {
    if (!isOpen || step !== "search") return;

    const handlePointerDown = (event) => {
      const wrap = clientSearchWrapRef.current;
      const dropdown = dropdownRef.current;
      if (wrap && wrap.contains(event.target)) return;
      if (dropdown && dropdown.contains(event.target)) return;
      setIsSearchDropdownOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsSearchDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, step]);

  const recalcClientDropdown = useCallback(() => {
    const wrapEl = clientSearchWrapRef.current;
    const panelEl = modalPanelRef.current;
    if (!wrapEl || !panelEl) return;
    const wrapRect = wrapEl.getBoundingClientRect();
    const panelRect = panelEl.getBoundingClientRect();
    const top = Math.floor(wrapRect.bottom + 8);
    const left = Math.floor(wrapRect.left);
    const width = Math.floor(wrapRect.width);
    setDropdownPos({ top, left, width });

    const padding = 16;
    const spaceBelowInPanel = panelRect.bottom - top - padding;
    const spaceBelowViewport = window.innerHeight - top - padding;
    const available = Math.min(spaceBelowInPanel, spaceBelowViewport);
    const MIN_H = 140;
    const MAX_H = 260;
    let next = Math.floor(Math.max(0, Math.min(MAX_H, Math.max(MIN_H, available))));
    next = Math.max(0, Math.min(next, Math.floor(available)));
    if (!Number.isFinite(next)) {
      next = MIN_H;
    }
    setDropdownMaxHeightPx(next);
  }, []);

  useLayoutEffect(() => {
    if (!shouldShowDropdown) return;
    recalcClientDropdown();
    window.addEventListener("resize", recalcClientDropdown);
    window.addEventListener("scroll", recalcClientDropdown, true);
    const panelEl = modalPanelRef.current;
    if (panelEl) {
      panelEl.addEventListener("scroll", recalcClientDropdown, true);
    }
    return () => {
      window.removeEventListener("resize", recalcClientDropdown);
      window.removeEventListener("scroll", recalcClientDropdown, true);
      if (panelEl) {
        panelEl.removeEventListener("scroll", recalcClientDropdown, true);
      }
    };
  }, [shouldShowDropdown, results.length, searching, searchError, recalcClientDropdown]);

  const handleSearchChange = (value) => {
    setQuery(value);
    setIsSearchDropdownOpen(true);
  };

  const openNewClientModal = (seedTerm = "") => {
    const digits10 = toNANPDigits10(seedTerm);
    const nextPrefill = digits10.length === 10 ? { phone: digits10 } : null;
    setNewClientPrefill(nextPrefill);
    setNewClientOpen(true);
    setIsSearchDropdownOpen(false);
  };

  const handleOpenNewClient = () => {
    openNewClientModal(trimmedSearch);
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setStep("confirm");
    setIsSearchDropdownOpen(false);
  };

  const handleChooseAnother = () => {
    setSelectedClient(null);
    setStep("search");
  };

  const handleCreateTicket = async () => {
    if (!selectedClient?.id) return;
    setCreateError("");
    try {
      const payload = {
        client_id: selectedClient.id,
        ticket_date: formatDateValue(ticketDate, salonTimezone),
        type: "sales",
        ticket_type: "sales",
        status: "open",
        appointment_type: appointmentType,
      };
      const ticket = await createTicket(payload);
      if (ticket?.id) {
        onClose();
        navigate(`/tickets/${ticket.id}`);
      } else {
        setCreateError("Unable to create ticket.");
      }
    } catch (err) {
      console.error(err);
      setCreateError(err?.message || "Unable to create ticket.");
    }
  };

  const handleTodayClick = () => {
    const today = new Date();
    setTicketDate(today);
    setCalendarViewDate(today);
  };

  const handleCreateClient = async (payload) => {
    const created = await createClient(payload);
    setSelectedClient(created);
    setStep("confirm");
    return created;
  };

  const handleOpenServiceLog = async () => {
    if (!selectedClient?.id) return;
    setServiceLogError("");
    setServiceLogLoading(true);
    try {
      const client = await getClientById(selectedClient.id);
      setServiceLogClient(client);
      setServiceLogOpen(true);
    } catch (err) {
      console.error(err);
      setServiceLogError("Unable to load service log.");
    } finally {
      setServiceLogLoading(false);
    }
  };

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canEditClients = ["admin", "host", "owner", "independent_professional"].includes(role);
  const hasMatches = results.length > 0;
  const showCreateClientOption = canEditClients && hasTerm && !hasMatches;
  const createPhoneLabel =
    termDigits10.length === 10 ? formatNANP(termDigits10) : "";
  const createHelperLabel = createPhoneLabel
    ? `Phone: ${createPhoneLabel}`
    : `Use: "${trimmedSearch}"`;
  const dropdownContent =
    shouldShowDropdown && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={dropdownRef}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white text-sm shadow-lg"
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 11060,
            }}
          >
            <div
              className="overflow-y-auto overscroll-contain"
              style={{ maxHeight: dropdownMaxHeightPx }}
            >
              {searching && (
                <div className="px-4 py-3 text-xs text-slate-500">
                  Searching...
                </div>
              )}
              {!searching && searchError && (
                <div className="px-4 py-3 text-xs text-rose-600">
                  {searchError}
                </div>
              )}
              {!searching && !searchError && hasMatches &&
                results.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    className="w-full px-4 py-3 text-left text-slate-700 hover:bg-slate-50"
                    onClick={() => handleSelectClient(client)}
                  >
                    <div className="font-medium text-slate-800">
                      {client.full_name || "Unnamed Client"}
                      {client.phone
                        ? ` - ${formatNANP(client.phone)}`
                        : ""}
                    </div>
                  </button>
                ))}
              {!searching && !searchError && !hasMatches && showCreateClientOption && (
                <button
                  type="button"
                  onClick={() => openNewClientModal(trimmedSearch)}
                  className="w-full text-left px-4 py-3 text-slate-800 hover:bg-slate-50"
                >
                  <div className="font-semibold text-slate-900">
                    + Create New Client
                  </div>
                  <div className="text-xs text-slate-500">
                    {createHelperLabel}
                  </div>
                </button>
              )}
              {!searching && !searchError && !hasMatches && !showCreateClientOption && (
                <div className="px-4 py-3 text-xs text-slate-500">
                  No matching clients.
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  if (!isOpen && !newClientOpen && !serviceLogOpen) return null;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[10040] flex items-start justify-center bg-black/60 px-4 pt-14 pb-6">
          <div
            ref={modalPanelRef}
            className={`w-full max-w-2xl rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col h-[85vh] max-h-[90vh] ${modalBase}`}
          >
            <div className="flex items-center justify-between bg-rose-500 px-6 py-4">
              <h2 className={`${modalTitle} text-white`}>New Ticket</h2>
              <ModalCloseX onClick={onClose} />
            </div>

            <div
              ref={modalBodyRef}
              className="px-6 py-5 space-y-4 flex-1 min-h-0 overflow-y-auto sbin-hide-scrollbar"
            >
              {step === "search" && (
                <div className="space-y-4">
                  <div>
                    <label className={`${labelCls} uppercase`}>Date</label>
                    <div className="relative mt-2">
                      <button
                        type="button"
                        onClick={() => setShowCalendar((prev) => !prev)}
                        className={`w-full ${fieldCls} text-left text-slate-800 hover:bg-slate-50`}
                        ref={calendarAnchorRef}
                      >
                        {formatDisplayDate(ticketDate)}
                      </button>
                      {showCalendar &&
                        createPortal(
                          <div
                            ref={calendarPopoverRef}
                            className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                            style={{
                              position: "absolute",
                              top: calendarPosition.top,
                              left: calendarPosition.left,
                              width: calendarPosition.width,
                              zIndex: 11050,
                            }}
                          >
                            <Calendar
                              onChange={(value) => {
                                const date = Array.isArray(value) ? value[0] : value;
                                setTicketDate(date);
                                setCalendarViewDate(date);
                                setShowCalendar(false);
                              }}
                              value={ticketDate}
                              activeStartDate={calendarViewDate}
                              onActiveStartDateChange={({ activeStartDate }) => {
                                if (activeStartDate) setCalendarViewDate(activeStartDate);
                              }}
                              calendarType="gregory"
                              minDetail="month"
                              maxDetail="month"
                              selectRange={false}
                              className="w-full"
                            />
                            <div className="mt-2 flex justify-end">
                              <button
                                type="button"
                                className="px-2 py-1 rounded border border-slate-300 text-xs hover:bg-slate-100"
                                onClick={handleTodayClick}
                              >
                                Today
                              </button>
                            </div>
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>

                  <div>
                    <div className={`${labelCls} uppercase`}>Client Search</div>
                    <div ref={clientSearchWrapRef} className="relative mt-2">
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onFocus={() => setIsSearchDropdownOpen(true)}
                        placeholder="Search by name or phone"
                        className={`w-full ${fieldCls}`}
                      />
                    </div>
                    <button
                      type="button"
                      className="mt-2 text-sm font-semibold text-slate-700 underline"
                      onClick={handleOpenNewClient}
                    >
                      New Client
                    </button>
                  </div>
                </div>
              )}

              {step === "confirm" && selectedClient && (
                <div className="space-y-4">
                  <div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {selectedClient.full_name || "Client"}
                    </div>
                    <div className="mt-1 text-base text-slate-600">
                      Mobile:{" "}
                      {selectedClient.phone
                        ? formatNANP(selectedClient.phone)
                        : "Not provided"}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      className={`${btnCls} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                      onClick={handleOpenServiceLog}
                      disabled={serviceLogLoading}
                    >
                      {serviceLogLoading ? "Loading..." : "Service Log"}
                    </button>
                  </div>

                  {serviceLogError && (
                    <div className="text-sm text-rose-600">{serviceLogError}</div>
                  )}

                  <div>
                    <label className={`${labelCls} uppercase`}>
                      Appointment Type
                    </label>
                    <select
                      value={appointmentType}
                      onChange={(e) => setAppointmentType(e.target.value)}
                      className={`mt-2 w-full ${fieldCls} focus:border-rose-500 focus:outline-none`}
                    >
                      {APPOINTMENT_TYPE_OPTIONS.filter(
                        (option) => option.value !== "no_show"
                      ).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {createError && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
                      {createError}
                    </div>
                  )}
                </div>
              )}
            </div>

            {step === "confirm" && (
              <div className="flex justify-between gap-3 border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  className={`${btnCls} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400`}
                  onClick={handleChooseAnother}
                >
                  Choose another Client
                </button>
                <button
                  type="button"
                  className={`${btnCls} font-semibold text-white transition bg-rose-500 hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed`}
                  onClick={handleCreateTicket}
                  disabled={!selectedClient}
                >
                  Create Ticket
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {dropdownContent}

      <NewClientModal
        isOpen={newClientOpen}
        mode="create"
        initialClient={newClientPrefill}
        salons={[]}
        canEdit={canEditClients}
        preferredSalonId={null}
        onClose={() => {
          setNewClientOpen(false);
          setNewClientPrefill(null);
        }}
        onSave={handleCreateClient}
      />
      <NewClientModal
        isOpen={serviceLogOpen}
        mode="edit"
        initialClient={serviceLogClient}
        salons={[]}
        canEdit={false}
        preferredSalonId={null}
        initialTab="services"
        onClose={() => {
          setServiceLogOpen(false);
          setServiceLogClient(null);
          setServiceLogError("");
        }}
        onSave={null}
      />
    </>
  );
}
